import { i18n, MessageDescriptor } from "@lingui/core";
import deepFreeze from "deep-freeze-es6";
import { msg } from "@lingui/core/macro";
import dayjs, { ManipulateType } from "dayjs";
import { Duration } from "dayjs/plugin/duration";

import colors from "../../styles/colors";
import {
  AccountingPointDTO,
  DataAcquisitionDTO,
  MediumDTO,
} from "../../lib/dto-schemas/energy-management";

export type EdgeDevice = Readonly<{
  id: string;
  type: "agent1" | "agent2";
}>;

export type Medium = Readonly<
  (
    | { type: "custom"; label: { translatable: false; value: string } }
    | {
        type: "default";
        label: { translatable: true; key: DefaultMediumId };
      }
  ) & {
    id: string;
    color: string;
    lightColor: string;
    colorName: string;
    dataAcquisitions: ReadonlyArray<DataAcquisition>;
  }
>;

export function findMedium(availableMedia: ReadonlyArray<Medium>, mediumId: string): Medium {
  const medium = availableMedia.find((m) => m.id === mediumId);
  if (medium == null) {
    throw new Error(`Unknown medium: ${mediumId}`);
  }
  return medium;
}

export function canHaveHeatingValue(medium: Medium): boolean {
  return medium.type === "custom" || (medium.type === "default" && medium.label.key === "gas");
}

export function hasHeatingValueDataAcquisition(medium: Medium): boolean {
  return medium.dataAcquisitions.some((da) => da.measurand.symbol.toString() === "H_s");
}

const defaultMediumOrder = ["electricity", "gas", "compressed air", "water", "heat"] as const;
type DefaultMediumId = (typeof defaultMediumOrder)[number];

function isDefaultMediumId(id: string): id is DefaultMediumId {
  return (defaultMediumOrder as ReadonlyArray<string>).includes(id);
}

export function makeMedium(
  type: "default" | "custom",
  id: string,
  label: string,
  colorName: string,
  dataAcquisitions: ReadonlyArray<DataAcquisition>
): Medium {
  if (!Object.hasOwn(colors, colorName)) {
    throw new Error(`Unknown color: ${colorName}`);
  }

  const color = colors[colorName as keyof typeof colors][600] as string;
  const lightColor = colors[colorName as keyof typeof colors][50] as string;

  if (type === "default" && isDefaultMediumId(id)) {
    return deepFreeze({
      type,
      id,
      label: { translatable: true, key: id },
      color,
      lightColor,
      colorName,
      dataAcquisitions,
    });
  } else {
    return deepFreeze({
      type: "custom",
      id,
      label: { translatable: false, value: label },
      color,
      lightColor,
      colorName,
      dataAcquisitions,
    });
  }
}

export function makeMediumFromDTO(dto: MediumDTO): Medium {
  return makeMedium(
    dto.type,
    dto.id,
    dto.label,
    dto.color,
    dto.dataAcquisitions.map((dacq) => {
      const medium = dacq.measurand.medium;
      const symbol = makeSymbol(dacq.measurand.symbol.name, dacq.measurand.symbol.subscript);
      const measurand = makeMeasurand(medium, symbol);
      if ("interval" in dacq && "source" in dacq) {
        return makeDataAcquisition(measurand, dacq.source, dayjs.duration(dacq.interval));
      } else {
        return makeDataAcquisition(measurand);
      }
    })
  );
}

export function mediumComparator(m1: string, m2: string): number;
export function mediumComparator(m1: Medium, m2: Medium): number;
export function mediumComparator(m1: Medium | string, m2: Medium | string): number {
  function isMedium(m: unknown): m is Medium {
    return m != null && typeof m === "object" && Object.hasOwn(m, "id");
  }

  function compareByDefaultOrder(
    rank1: number,
    rank2: number,
    fallbackCompare: () => number
  ): number {
    const isDefault1 = rank1 !== -1;
    const isDefault2 = rank2 !== -1;
    if (isDefault1 && isDefault2) {
      return rank1 - rank2;
    } else if (isDefault1) {
      return -1;
    } else if (isDefault2) {
      return 1;
    }
    return fallbackCompare();
  }

  const order = defaultMediumOrder as ReadonlyArray<string>;
  if (isMedium(m1) && isMedium(m2)) {
    const text = (m: Medium): string => (m.label.translatable ? m.label.key : m.label.value);
    return compareByDefaultOrder(order.indexOf(m1.id), order.indexOf(m2.id), () =>
      text(m1).localeCompare(text(m2))
    );
  } else if (typeof m1 === "string" && typeof m2 === "string") {
    return compareByDefaultOrder(order.indexOf(m1), order.indexOf(m2), () => m1.localeCompare(m2));
  } else {
    throw Error("Cannot compare media.");
  }
}

export type Symbol = Readonly<{
  name: string;
  subscript?: string;
  toString: () => string;
  label: MessageDescriptor;
}>;

export const symbolLabels: Record<string, MessageDescriptor> = {
  U_L1: msg`Voltage L1`,
  U_L2: msg`Voltage L2`,
  U_L3: msg`Voltage L3`,
  I_L1: msg`Current L1`,
  I_L2: msg`Current L2`,
  I_L3: msg`Current L3`,
  I_N: msg`Current N`,
  P: msg`Active Power`,
  Q: msg`Reactive Power`,
  S: msg`Apparent Power`,
  "E_P,IMPORT": msg`Active Energy (Import)`,
  "E_P,EXPORT": msg`Active Energy (Export)`,
  "E_Q,IMPORT": msg`Reactive Energy (Import)`,
  "E_Q,EXPORT": msg`Reactive Energy (Export)`,
  λ: msg`Power Factor`,
  λ_L1: msg`Power Factor L1`,
  λ_L2: msg`Power Factor L2`,
  λ_L3: msg`Power Factor L3`,
  cosφ: msg`Active Factor`,
  cosφ_L1: msg`Active Factor L1`,
  cosφ_L2: msg`Active Factor L2`,
  cosφ_L3: msg`Active Factor L3`,
  P_MAX: msg`Month Peak Load`,
  P_L1: msg`Active Power L1`,
  P_L2: msg`Active Power L2`,
  P_L3: msg`Active Power L3`,
  "E_th,IMPORT": msg`Thermal Energy (Import)`,
  "E_th,EXPORT": msg`Thermal Energy (Export)`,
  E_IMPORT: msg`Energy (Import)`,
  E_EXPORT: msg`Energy (Export)`,
  "V_n,IMPORT": msg`Standard Volume (Import)`,
  "V_n,EXPORT": msg`Standard Volume (Export)`,
  V_IMPORT: msg`Volume (Import)`,
  V_EXPORT: msg`Volume (Export)`,
  p: msg`Pressure`,
  Δp: msg`Pressure Difference`,
  q_V: msg`Volume Flow`,
  q_m: msg`Mass Flow`,
  m: msg`Mass`,
  P_th: msg`Thermal Power`,
  T: msg`Temperature`,
  t_DUTY: msg`Operating Time`,
  T_FLOW: msg`Flow Temperature`,
  T_RETURN: msg`Return Temperature`,
  H_s: msg`Heating value`,
};

export function makeSymbol(name: string, subscript?: string): Symbol {
  function toString() {
    return subscript == null ? name : `${name}_${subscript}`;
  }

  if (!symbolLabels[toString()]) {
    throw new Error(`Unknown symbol: ${toString()}`);
  }

  return deepFreeze({ name, subscript, toString, label: symbolLabels[toString()] });
}

makeSymbol.fromString = function fromString(symbolStr: string) {
  const [name, subscript] = symbolStr.split("_");
  return makeSymbol(name, subscript);
};

export type Measurand = Readonly<{
  medium: string;
  symbol: Symbol;
  isIncremental: () => boolean;
}>;

export function makeMeasurand(medium: string, symbol: Symbol): Measurand {
  function isIncremental(): boolean {
    return ["E", "V", "m", "t"].includes(symbol.name);
  }

  return deepFreeze({ medium, symbol, isIncremental });
}

export function symbolComparator(s1: Symbol, s2: Symbol): number {
  return i18n._(s1.label).localeCompare(i18n._(s2.label));
}

export type DataAcquisition = Readonly<{
  measurand: Measurand;
  interval?: Readonly<Duration>;
  source?: string;
  stop: () => DataAcquisition;
  isOngoing: () => boolean;
  isCalculated: () => boolean;
}>;

export function makeDataAcquisition(measurand: Measurand): DataAcquisition;
export function makeDataAcquisition(measurand: Measurand, source: "calculated"): DataAcquisition;
export function makeDataAcquisition(
  measurand: Measurand,
  source: string,
  interval: Duration
): DataAcquisition;
export function makeDataAcquisition(
  measurand: Measurand,
  source?: string,
  interval?: Duration
): DataAcquisition {
  function stop() {
    return makeDataAcquisition(measurand);
  }

  function isCalculated() {
    return source === "calculated";
  }

  function isOngoing() {
    return source != null && !isCalculated();
  }

  return deepFreeze({ source, stop, interval, measurand, isOngoing, isCalculated });
}

makeDataAcquisition.withDefaultInterval = function (measurand: Measurand, source: string) {
  const interval = dayjs.duration(symbolIntervalMap[measurand.symbol.toString()]);
  return makeDataAcquisition(measurand, source, interval);
};

export function dataAcquisitionToDTO(dacq: DataAcquisition): DataAcquisitionDTO {
  if ("source" in dacq && "interval" in dacq) {
    return {
      source: dacq.source,
      interval: dacq.interval?.toISOString(),
      measurand: {
        symbol: { name: dacq.measurand.symbol.name, subscript: dacq.measurand.symbol.subscript },
        medium: dacq.measurand.medium,
      },
    };
  } else {
    return {
      measurand: {
        symbol: { name: dacq.measurand.symbol.name, subscript: dacq.measurand.symbol.subscript },
        medium: dacq.measurand.medium,
      },
    };
  }
}

export function dataAcquisitionComparator(dacq1: DataAcquisition, dacq2: DataAcquisition) {
  return symbolComparator(dacq1.measurand.symbol, dacq2.measurand.symbol);
}

export const symbolIntervalMap: Record<string, string> = {
  U_L1: "PT15S",
  U_L2: "PT15S",
  U_L3: "PT15S",
  I_L1: "PT15S",
  I_L2: "PT15S",
  I_L3: "PT15S",
  I_N: "PT15S",
  P: "PT15S",
  Q: "PT15S",
  S: "PT15S",
  "E_P,IMPORT": "PT5M",
  "E_P,EXPORT": "PT5M",
  "E_Q,IMPORT": "PT5M",
  "E_Q,EXPORT": "PT5M",
  λ: "PT1M",
  λ_L1: "PT1M",
  λ_L2: "PT1M",
  λ_L3: "PT1M",
  cosφ: "PT1M",
  cosφ_L1: "PT1M",
  cosφ_L2: "PT1M",
  cosφ_L3: "PT1M",
  P_MAX: "PT15M",
  P_L1: "PT1M",
  P_L2: "PT1M",
  P_L3: "PT1M",
  "E_th,IMPORT": "PT5M",
  "E_th,EXPORT": "PT5M",
  "V_n,IMPORT": "PT5M",
  "V_n,EXPORT": "PT5M",
  E_IMPORT: "PT5M",
  E_EXPORT: "PT5M",
  V_IMPORT: "PT5M",
  V_EXPORT: "PT5M",
  p: "PT15S",
  Δp: "PT15S",
  q_V: "PT15S",
  q_m: "PT15S",
  m: "PT5M",
  P_th: "PT15S",
  T: "PT5M",
  t_DUTY: "PT5M",
  T_FLOW: "PT5M",
  T_RETURN: "PT5M",
};

export type TimeRange = Readonly<{
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  diffInDays: () => number;
  previous: () => TimeRange;
  next: () => TimeRange;
  isoformat: () => string;
}>;

export function makeTimeRange(start: dayjs.Dayjs, end: dayjs.Dayjs): TimeRange {
  if (end.isBefore(start)) {
    throw new Error("Start date must be before end date");
  }

  function diffInDays() {
    return end.diff(start, "days");
  }

  function integerDiffInLargestUnit(start: dayjs.Dayjs, end: dayjs.Dayjs) {
    const decreasingTimeUnits: Array<ManipulateType> = ["months", "days"];

    for (const unit of decreasingTimeUnits) {
      const exactDiff = end.diff(start, unit, true);
      const flooredDiff = end.diff(start, unit);
      if (exactDiff === flooredDiff) {
        return { value: exactDiff, unit: unit };
      }
    }

    return {
      value: end.diff(start, "milliseconds"),
      unit: "milliseconds" as ManipulateType,
    };
  }

  function previous() {
    const { value, unit } = integerDiffInLargestUnit(start, end);
    return makeTimeRange(start.subtract(value, unit), end.subtract(value, unit));
  }

  function next() {
    const { value, unit } = integerDiffInLargestUnit(start, end);
    return makeTimeRange(start.add(value, unit), end.add(value, unit));
  }

  function isoformat() {
    return `${start.format("YYYY-MM-DDTHH:mm:ss")}/${end.format("YYYY-MM-DDTHH:mm:ss")}`;
  }

  return deepFreeze({ start, end, diffInDays, previous, next, isoformat });
}

export type AccountingPoint = ReturnType<typeof makeAccountingPoint>;

export function makeAccountingPoint(id: string, name: string, measurands: Array<Measurand>) {
  return deepFreeze({ id, name, measurands });
}

export function makeAccountingPointFromDTO(dto: AccountingPointDTO): AccountingPoint {
  return makeAccountingPoint(
    dto.id,
    dto.name,
    dto.measurands.map(({ medium, symbol }) =>
      makeMeasurand(medium, makeSymbol(symbol.name, symbol.subscript))
    )
  );
}
